import { useId } from 'react';
import type { EvidenceReadiness } from '../lib/evidenceReadiness.js';

/**
 * Missing-evidence soft gate.
 *
 * AD-27 found all 43 pending production practice items carrying `sourceRefs: []`, so the Academic
 * Director reviewed exam items with nothing to check them against. AD-30 decision 2 chose a SOFT
 * gate over a hard one — Scott called provenance valuable but explicitly **not** a blocker, and a
 * hard gate on a cohort that cannot yet satisfy it is a work stoppage. AD-33 decision 3 narrowed the
 * acknowledgement to a **checkbox**: no note field, no reason, nothing recorded beyond the tick.
 *
 * One component for both surfaces so the wording cannot drift between the review page and the queue
 * row. Deliberately NOT `role="alert"`: an assertive announcement on every load of a cohort where
 * this is the normal state is noise, and the checkbox is in the tab order anyway (gpt-5.6-sol,
 * 2026-07-30).
 */
export function EvidenceGate({
  acknowledged,
  onAcknowledge,
  readiness,
  variant = 'page',
}: {
  acknowledged: boolean;
  onAcknowledge: (acknowledged: boolean) => void;
  readiness: EvidenceReadiness;
  variant?: 'page' | 'row';
}) {
  const headingId = useId();
  const body = readiness === 'none'
    ? 'This item cites no curriculum source, so its claims cannot be checked against the reading.'
    : 'Nothing this item cites can be opened as curriculum evidence, so its claims cannot be checked against the reading.';

  return (
    <section
      aria-labelledby={headingId}
      className={variant === 'page'
        ? 'mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5'
        : 'rounded-xl border border-amber-300 bg-amber-50 p-3'}
      data-testid="evidence-gate"
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="material-symbols-outlined text-amber-600">report</span>
        <div className="min-w-0">
          <h3
            className={`font-black uppercase tracking-wide text-amber-900 ${variant === 'page' ? 'text-sm' : 'text-xs'}`}
            id={headingId}
          >
            No curriculum evidence to check this against
          </h3>
          <p className={`mt-1 leading-relaxed text-amber-900 ${variant === 'page' ? 'text-sm' : 'text-xs'}`}>
            {body}
          </p>
          <label className={`mt-3 flex items-center gap-2 font-bold text-amber-900 ${variant === 'page' ? 'text-sm' : 'text-xs'}`}>
            <input
              checked={acknowledged}
              className="size-4 rounded border-amber-400 text-amber-600 focus-visible:ring-2 focus-visible:ring-amber-500"
              onChange={(event) => onAcknowledge(event.target.checked)}
              type="checkbox"
            />
            I am approving without checkable evidence.
          </label>
          <p className="mt-2 text-xs text-amber-800">
            You can still reject or comment without acknowledging this.
          </p>
        </div>
      </div>
    </section>
  );
}
